import type { IPromiseBasedObservable } from "mobx-utils";
import type { TFunction } from "i18next";
import type { TableSort } from "@whatslly/ui-components/v2/Tables/Table/TableTypes";
import type { ClientSideSidecar, channelTypeOptions } from "@whatslly/types/sidecars-server/Sidecar";
import type { SupportedSidecarChannel } from "@whatslly/commons/sidecarUtils";
import type { SidecarCommandRequest, SidecarJobStatus } from "@whatslly/types/sidecars-server/SidecarCommands";
import type { PairingIdentification } from "./types";

export interface ISidecarsStore {
  // Observable properties
  _sidecars: Array<ClientSideSidecar>;
  default_sidecar_frequency: string;
  sidecarsUpdating: boolean;
  isInitialized: boolean;
  clearQueryCacheFromPromise?: IPromiseBasedObservable<undefined>;
  selectedItems: Array<ClientSideSidecar>;
  bulkDeleteProgress: { current: number; total: number } | null;

  // Computed properties
  sort: TableSort;
  channel: SupportedSidecarChannel | undefined;
  urlSearchParams: IPromiseBasedObservable<string>;
  searchTerm: string;
  statusFilters: Set<string>;
  pairStatusFilters: Set<string>;
  runnerFilter: Set<string>;
  groupsFilter: Set<string>;
  maxDevicesForChannel: number;
  availableChannelsForCreation: Array<(typeof channelTypeOptions)[number]>;
  canCreateSidecar: boolean;
  filteredSidecars: Array<ClientSideSidecar>;
  limitDateRangeOfMessages: number | undefined;

  // Methods
  getMaxDevicesForChannel(channel: SupportedSidecarChannel): number;
  hasAvailableSlotsForChannel(channel: SupportedSidecarChannel): boolean;
  canCreateSidecarForChannel(channel?: SupportedSidecarChannel): boolean;
  downloadCsvFile(t: TFunction): Promise<void>;
  togglePairStatusFilter(status: string): void;
  updateSidecars(): Promise<void>;
  createSidecar(user?: { _id: string }, channel?: SupportedSidecarChannel): Promise<void>;
  restoreLatestSnapshot(sidecarId: string): Promise<void>;
  reinviteSidecar(sidecarId: string): Promise<void>;
  runCommand(
    sidecarId: string,
    identification: PairingIdentification,
    command: SidecarCommandRequest
  ): Promise<(SidecarJobStatus & { success: true })["result"] | undefined>;
  selectCommand(sidecar: ClientSideSidecar): Promise<void>;
  startSidecar(sidecarId: string, verbose?: boolean, resyncAllMessages?: boolean): Promise<void>;
  clearQueryCache(): Promise<void>;
  sidecarSettings(sidecar: ClientSideSidecar): Promise<void>;
  deleteSidecar(sidecarId: string, t: TFunction): Promise<void>;
  bulkReinviteSidecars(sidecarIds: string[], t: TFunction): Promise<void>;
  bulkDeleteSidecars(sidecarIds: string[], t: TFunction): Promise<void>;
  toggleItemSelection(sidecar: ClientSideSidecar): void;
  deselectAllItems(): void;
  setSearchTerm(searchTerm: string): void;
  toggleStatusFilter(status: string): void;
  toggleRunnerFilter(runner: string): void;
  toggleGroupsFilter(group: string): void;
}

export const InjectSidecarsStore = Symbol.for("SidecarsStore");
